import {parseVersion} from '@/utils/otaVersion';

export type NativePlatform = 'ios' | 'android';

export interface AppUpdate {
  version: string;
  url: string;
  checksum: string;
  platform: NativePlatform | null;
  min_native_version: string | null;
}

interface AppUpdateResponse {
  update: AppUpdate | null;
}

export function isAppUpdateResponse(value: unknown, platform: NativePlatform): value is AppUpdateResponse {
  if (!value || typeof value !== 'object' || !('update' in value)) return false;
  const update = (value as AppUpdateResponse).update;
  if (update === null) return true;
  if (!update || typeof update !== 'object') return false;
  if (typeof update.version !== 'string' || !parseVersion(update.version)) return false;
  if (typeof update.checksum !== 'string' || !/^[a-f0-9]{64}$/.test(update.checksum)) return false;
  if (update.platform !== null && update.platform !== platform) return false;
  if (update.min_native_version !== null &&
    (typeof update.min_native_version !== 'string' || !parseVersion(update.min_native_version))) return false;

  try {
    const url = new URL(update.url);
    const frontend = new URL(import.meta.env.VITE_APP_URL);
    return url.protocol === 'https:' && url.username === '' && url.password === '' &&
      url.origin === frontend.origin && url.pathname === `${frontend.pathname.replace(/\/$/, '')}/zips/${update.version}.zip` &&
      url.search === '' && url.hash === '';
  } catch {
    return false;
  }
}

export async function checkForUpdate(platform: NativePlatform): Promise<AppUpdate | null> {
  const endpoint = import.meta.env.VITE_APP_UPDATE_URL;
  if (!endpoint) {
    console.warn('[OTA] update endpoint is not configured');
    return null;
  }

  const url = new URL(endpoint);
  if (url.protocol !== 'https:' || url.username || url.password) {
    throw new Error('OTA endpoint must use HTTPS without URL credentials');
  }
  url.searchParams.set('platform', platform);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 7000);

  try {
    console.info('[OTA] checking for update');
    const response = await fetch(url, {signal: controller.signal, cache: 'no-store'});
    if (!response.ok) throw new Error(`Update endpoint returned HTTP ${response.status}`);
    const data: unknown = await response.json();
    if (!isAppUpdateResponse(data, platform)) throw new Error('Malformed update response');
    if (data.update === null) console.info('[OTA] no update available');
    return data.update;
  } finally {
    clearTimeout(timeout);
  }
}
